import { Component, ElementRef, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { CreateTenantUserModal } from '../../modals/create-tenant-user/create-tenant-user.modal';
import { EditTenantUserModal } from '../../modals/edit-tenant-user/edit-tenant-user.modal';
import { UserModel } from '../../../../authentication/models/user.model';
import { TenantUserService } from '../../../../authentication/services/tenant-user.service';
import { ChassisFormHandler } from '../../../../forms/form/classes/form-handler';
import { TenantModel } from '../../../../authentication/models/tenant.model';
import { Collection } from '../../../../support/collection/collection';
import { ToastService } from '../../../../toast/services/toast.service';
import { ModalService } from '../../../../modals/services/modal.service';
import { AlertService } from '../../../../alerts/services/alert.service';
import { ApiCollection } from '../../../../api/collection';
import { Model } from '../../../../api/model';

@Component({
    selector: 'chassis-core-tenant-user-plugin',
    templateUrl: 'tenant-user.plugin.html',
    styleUrls: ['tenant-user.plugin.scss'],
    providers: [TenantUserService]
})
export class TenantUserPlugin implements OnInit {
    /**
     * The panel to place the notifications on.
     *
     * @type {ElementRef}
     */
    @ViewChild('panel') panel: ElementRef;

    /**
     * Chassis form handler instance injected by the plugin slot, containing the form handler from the page.
     *
     * @type {ChassisFormHandler}
     */
    @Input() public form: ChassisFormHandler;

    /**
     * Observable containing the page's model instance.
     *
     * @type {Subject<any>}
     */
    @Input() public modelSubject: Subject<TenantModel>;

    /**
     * The collections of items.
     *
     * @type {Collection}
     */
    public items: Collection = new Collection;

    /**
     * The colums to display in the table
     */
    public tableColumns;

    /**
     * The loading indicator
     * @type {boolean}
     */
    public loading = true;

    /**
     * The AlertService instance.
     *
     * @type {AlertService}
     */
    private alertService: AlertService;

    /**
     * The ModalService instance.
     *
     * @type {ModalService}
     */
    private modalService: ModalService;

    /**
     * The ToastService instance.
     *
     * @type {ToastService}
     */
    private toastService: ToastService;


    /**
     * The TenantService instance.
     *
     * @type {TenantService}
     */
    private tenantUserService: TenantUserService;

    constructor(injector: Injector) {
        this.alertService      = injector.get(AlertService);
        this.modalService      = injector.get(ModalService);
        this.toastService      = injector.get(ToastService);
        this.tenantUserService = injector.get(TenantUserService);
    }

    ngOnInit() {
        this.setTableColumns();

        this.modelSubject.subscribe((model: TenantModel) => {
            if (model.exists()) {
                this.tenantUserService.defaultParams = {tenantId: model.id};
                this.tenantUserService.all().subscribe((items: ApiCollection) => {
                    this.items = new Collection(items.all());

                    this.loading = false;
                });
            } else {
                this.form.success((model, resolve, reject) => {
                    this.tenantUserService.defaultParams = {tenantId: model.id};
                    this.postItemsAfterParentCreated(resolve, reject);
                });

                this.loading = false;
            }
        });
    }

    /**
     * Open the add modal.
     */
    public openAddTenantUserModal() {
        const modal = this.modalService.open(CreateTenantUserModal, {}, {
            classes: 'slide-top',
        });

        modal.beforeClose((resolve, reject, item) => {
            this.modelSubject.subscribe((model: Model) => {
                if (model.exists()) {
                    item = this.tenantUserService.make(item.plain());
                    item.save().subscribe((result: UserModel) => {
                        this.toastService.success('success', 'User attached successfully', {element: this.panel.nativeElement});

                        resolve(result);
                    }, (error) => {
                        this.toastService.error('error', 'Failed to attach tenant', {element: this.panel.nativeElement});

                        reject(error);
                    });
                } else {
                    this.toastService.info('info', 'User added successfully', {element: this.panel.nativeElement});

                    resolve(item);
                }
            });
        });

        modal.result.then((result: TenantModel) => {
            this.items.push(result);
        }, (reason) => {
        });
    }

    /**
     * Open the edit modal.
     *
     * @param {TenantModel} model
     */
    public openEditTenantUserModal(model: TenantModel) {
        const modal = this.modalService.open(EditTenantUserModal, {
            user: model.clone()
        }, {
            classes: 'slide-top'
        });

        modal.closing((resolve, reject, result) => {
            return this.beforeCloseCallback(resolve, reject, result);
        });

        modal.dismissing((resolve, reject, reason) => {
            if (reason === 'delete') {
                return this.beforeDismissDeleteCallback(resolve, reject, reason, model);
            } else {
                resolve(reason);
            }
        });

        modal.result.then((result: any) => {
            model.fill(result);
        }, (reason) => {
            if (reason === 'delete') {
                this.items = this.items.filter((item) => {
                    return item !== model;
                });
            }
        });
    }

    /**
     * Set datatable columns
     */
    private setTableColumns() {
        this.tableColumns = [
            {name: 'name', width: '50%'},
            {name: 'email', title: 'email', width: '50%'}
        ];
    }

    /**
     * Post all items after the model in the main form has been created.
     *
     * @param resolve
     * @param reject
     */
    private postItemsAfterParentCreated(resolve, reject) {
        if (!this.items.length) {
            resolve();
        }

        this.items.forEach((tenant: TenantModel, index: number) => {
            this.loading = true;
            tenant       = this.tenantUserService.make(tenant.plain());
            tenant.save().subscribe((savedTenant: TenantModel) => {
                if (index === this.items.length - 1) {
                    this.toastService.success('success', 'Tenant attached successfully.', {element: this.panel.nativeElement});

                    this.loading = false;

                    resolve();
                }
            }, (error) => {
                this.toastService.error('error', 'Failed to attach tenant', {element: this.panel.nativeElement});

                this.loading = false;

                reject();
            });
        });
    }

    /**
     * Saves the instance returned from the modal.
     *
     * @param {Function} resolve
     * @param {Function} reject
     * @param {TenantModel} tenantModel
     */
    private beforeCloseCallback(resolve: Function, reject: Function, tenantModel: TenantModel) {
        if (tenantModel.exists()) {
            tenantModel.save().subscribe((result) => {
                this.toastService.success('success', 'Tenant saved successfully', {element: this.panel.nativeElement});

                resolve(result);
            });
        } else {
            resolve(tenantModel);
        }
    }

    /**
     * Deletes the instance returned from the modal.
     *
     * @param {Function} resolve
     * @param {Function} reject
     * @param reason
     * @param {TenantModel} tenantModel
     */
    private beforeDismissDeleteCallback(resolve: Function, reject: Function, reason: any, tenantModel: TenantModel) {
        const prompt = this.alertService.open({
            title: 'Are you sure?',
        });

        prompt.result.then(() => {

            this.modelSubject.subscribe((model: Model) => {
                if (model.exists()) {
                    this.tenantUserService.delete(tenantModel.id).subscribe((deletedTenant) => {
                        this.toastService.success('success', 'Tenant detached successfully', {element: this.panel.nativeElement});

                        resolve(reason);
                    }, (error) => {
                        this.toastService.error('error', 'Failed to detach tenant', {element: this.panel.nativeElement});

                        reject(error);
                    });
                } else {
                    this.toastService.info('info', 'Tenant removed successfully', {element: this.panel.nativeElement});

                    resolve(reason);
                }
            });
        }, (error) => {
            reject(error);
        });
    }
}


// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/pages/tenants/plugins/tenant-user/tenant-user.plugin.ts